/**
 * El indicador de guardado.
 *
 * RF-015 exige saber **en todo momento** si el trabajo está guardado, hay cambios pendientes o
 * algo falló. «En todo momento» incluye a quien usa un lector de pantalla: por eso el texto
 * visible **no** está oculto a la tecnología asistiva, y la región de anuncios se limita a las
 * transiciones. Ocultar el texto y anunciar solo los cambios dejaría a esa persona sin forma de
 * preguntar «¿está guardado?».
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SaveState } from '../content/autosave.js';
import { Aviso } from './sistema/aviso.js';
import { Boton } from './sistema/boton.js';
import { Glifo, type NombreGlifo } from './sistema/glifo.js';

const GLIFOS: Record<SaveState, NombreGlifo> = {
  saved: 'correcto',
  pending: 'lleno',
  saving: 'girando',
  failed: 'aviso',
  conflict: 'aviso',
};

export function IndicadorGuardado({ state }: { state: SaveState }): React.JSX.Element {
  const { t } = useTranslation();
  const [announcement, setAnnouncement] = useState('');
  const previous = useRef<SaveState>(state);

  // El anuncio se calcula en un efecto, no durante el render: React prohíbe leer y escribir
  // referencias mientras se renderiza, y en modo estricto el render ocurre dos veces, con lo que
  // la segunda pasada no vería ningún cambio.
  useEffect(() => {
    if (previous.current === state) return;
    previous.current = state;
    // Solo se anuncian los estados finales: «guardando» y «pendiente» cambian constantemente
    // mientras se escribe, y anunciarlos sería insoportable.
    if (state === 'saved' || state === 'failed' || state === 'conflict') {
      setAnnouncement(t(`save.${state}`));
    }
  }, [state, t]);

  return (
    <>
      {/* Visible Y legible por lector de pantalla: es el estado permanente que exige RF-015. */}
      <p className="text-micro font-display flex items-center gap-1.5 font-bold uppercase">
        <Glifo
          nombre={GLIFOS[state]}
          size={16}
          className={state === 'saving' ? 'animate-spin' : undefined}
        />
        <span className={state === 'failed' || state === 'conflict' ? 'text-ko' : 'text-text-dim'}>
          {t(`save.${state}`)}
        </span>
      </p>

      {/* Solo las transiciones, y con cortesía: no interrumpe lo que se esté leyendo. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </>
  );
}

/**
 * El aviso de conflicto, que sí interrumpe: hay trabajo de otra persona en juego.
 *
 * Dice de qué pregunta habla. Antes era uno solo bajo el título, sin decirlo, y con dos filas en
 * conflicto solo se veía una.
 *
 * Y dice la verdad sobre lo que pasa al recargar. El texto anterior prometía «Lo tuyo sigue
 * aquí», y no era cierto: lo tecleado vive en un campo privado en memoria, así que recargar lo
 * tira. Prometer que algo está a salvo cuando no lo está es peor que no decir nada.
 */
export function AvisoConflicto({
  donde,
  onReload,
}: {
  donde: string;
  onReload: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Aviso tono="error" interrumpe className="mt-4">
      <p className="font-display font-bold">{t('save.conflictIn', { where: donde })}</p>
      <p className="text-body text-text-dim mt-1">{t('save.conflictLead')}</p>
      <Boton variante="secundaria" className="mt-3" onClick={onReload}>
        {t('save.reload')}
      </Boton>
    </Aviso>
  );
}
